// apps/web/src/widgets/option-tier-matrix/renderer.tsx
//
// Phase 11.5 — REND-01 OptionTierMatrixRenderer. Composes onto a sibling
// option-set widget via config.optionSetWidgetId (the FK across widget instances
// in the same recipe — mirrors ranked-tier/renderer.tsx). For each SELECTED
// source option it renders that option's config.options[id].rows as a per-option
// single-select role="radiogroup" of role="radio" tiles. Clicking a tile writes
// chosenRowByOptionId[optionId] = rowId via onStateChange (auto-persisted by the
// 11.3 provider).
//
// State shape: { chosenRowByOptionId: Readonly<Record<string,string>> } (Plan 01
// type-map). This is the FIRST compose-renderer that ALSO carries its OWN
// interactive state — ranked-tier/branch-variant are read-only display — so it
// blends ranked-tier's compose/guard/shell skeleton with scaling-matrix's
// own-state record-spread write (RESEARCH Pitfall 4). `chosen` (own state) is
// DELIBERATELY distinct from `selectedIds` (the source widget's option
// selection) — do NOT conflate them.
//
// Compose discipline (Rules of Hooks — RESEARCH Pitfall 2 / PATTERNS §SP-5): the
// source-state hook is called ONCE at the top level on config.optionSetWidgetId,
// never per-option (the single call site is the only non-import reference in
// this file). The source `options[]` are read via the shared pure
// readSourceOptions helper (Plan 01) — NOT re-derived inline.
//
// Data-driven-CSS bridge (UI-SPEC §"Selected-row styling"; PATTERNS §SP-4): the
// SELECTED tile gets an inline `--recipe-row-bg`/`--recipe-row-fg` pair from the
// recipe's POSITIONAL config.rowColors[rowIndex] (a flat array, index-aligned to
// the option's rows[] — NOT keyed by row id; schema header :19/:99), consumed by
// the static .recipe-row-accent-bg / .recipe-row-accent-fg classes in globals.css.
// The border uses the accent TOKEN (border-[var(--recipe-accent)]), NOT the
// bridge var. Selection is NOT color-only: aria-checked + accent border + bold
// label are the non-color signals (a11y).
//
// C1 generality (PITFALLS §N3 / scripts/grep-no-recipe-identifiers.sh): every
// row id, row label, and cell string flows from `config` — the renderer NEVER
// hardcodes a row vocabulary (no `best`/`good`/`ok`/`bad` literal in this body;
// those grade words are recipe DATA in the author's config). The single-var
// bridge classes carry the same C1 exemption as .tier-rank-* (no recipe word in
// the class name).
//
// Sanitization: rowColors are recipe content; OptionTierMatrixConfigSchema
// Pass 2A enforces the CSS-color positive-list (rejecting <,>,;,expression(,url(,
// javascript:,var(,attr()) BEFORE the recipe reaches this renderer. The browser
// CSS parser rejects any non-color token at apply time; Helmet CSP `style-src
// 'self' 'unsafe-inline'` covers the inline style attribute. The value is
// assigned ONLY to the --recipe-row-bg/-fg custom properties (never to an
// arbitrary CSS declaration) — no break-out into another declaration.
//
// isolatedDeclarations + verbatimModuleSyntax (D-15): React's CSSProperties type
// is closed and does NOT admit `--var` keys; the documented escape hatch is a
// `Record<string,string>` object with an `as CSSProperties` cast (mirror
// ranked-tier/tier-css.ts:35-39).

import type { OptionSetConfig, OptionTierMatrixOption } from "@the-recipe-spread/shared";
import type { CSSProperties } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useOtherWidgetState } from "../use-other-widget-state.js";
import { readSourceOptions } from "../use-source-selection.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";

// Shared Card-shell + heading fallback used by the two graceful-degrade branches
// (missing source / empty options). Byte-identical copy + typography to
// ranked-tier/renderer.tsx:94-130 so the two renderers degrade the same way.
function FallbackShell({
  labelId,
  heading,
  message,
}: {
  labelId: string;
  heading: string;
  message: string;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {heading}
          </h3>
          <p className="text-sm text-[var(--recipe-muted)]">{message}</p>
        </section>
      </CardContent>
    </Card>
  );
}

export function OptionTierMatrixRenderer({
  widget,
  config,
  recipe,
  state,
  onStateChange,
}: WidgetRendererProps<"option-tier-matrix">): React.ReactElement {
  // Own interactive state (Pitfall 4 — distinct from selectedIds below).
  const chosen: Readonly<Record<string, string>> = state?.chosenRowByOptionId ?? {};

  // Compose: read the source option-set's `selected` ids via the engine hook
  // (ONE top-level call — Rules of Hooks). Source state lives in the provider
  // (mutable); source options live in the recipe (immutable post-validation).
  const sourceState = useOtherWidgetState(config.optionSetWidgetId);
  const rawSelectedIds: ReadonlyArray<string> =
    sourceState !== undefined && "selected" in sourceState ? sourceState.selected : [];
  // WR-01: dedupe (first-occurrence order) before the iteration below. The
  // route gate (WidgetInteractionStateSchema option-set variant) does NOT
  // enforce uniqueness on `selected`, so a crafted/legacy persisted blob can
  // feed duplicate ids; mapping them directly would emit duplicate React keys.
  // Immutable — a new array; sourceState.selected is never mutated.
  const selectedIds: ReadonlyArray<string> = Array.from(new Set(rawSelectedIds));

  // Source-option lookup via the shared pure helper (Plan 01). null = source
  // widget missing / wrong templateId / non-array options → graceful fallback.
  const sourceOptions = readSourceOptions(recipe.widgets ?? [], config.optionSetWidgetId);

  const labelId = `${widget.instanceId}-label`;

  // Single-select record write: clicking the already-chosen row is a no-op
  // (mirror scaling-matrix:106-113 click-same guard). Immutable whole-state
  // replace — copy the map, set the one key (PATTERNS §SP-3).
  const selectRow = (optionId: string, rowId: string): void => {
    if (chosen[optionId] === rowId) return;
    onStateChange({ chosenRowByOptionId: { ...chosen, [optionId]: rowId } });
  };

  // (1) Source missing/malformed → fallback (byte-identical to ranked-tier:125).
  if (sourceOptions === null) {
    return (
      <FallbackShell
        labelId={labelId}
        heading={widget.name}
        message="Source option-set widget missing."
      />
    );
  }

  // (2) No options to score → fallback.
  if (config.options.length === 0) {
    return <FallbackShell labelId={labelId} heading={widget.name} message="No options to score." />;
  }

  // (3) Populated. Iterate SELECTED source options (in source-selection order);
  // for each, find its config.options entry by id (silent-skip if absent —
  // mirror ranked-tier's misconfigured-bucket drop at :147-153). Render each
  // option's rows (declaration order — NO sort; matrix has no rank field and
  // rowColors[rowIndex] is index-aligned) as a single-select radiogroup.
  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-4">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {widget.name}
          </h3>
          {selectedIds.map((optionId) => {
            const matched: OptionTierMatrixOption | undefined = config.options.find(
              (o) => o.id === optionId,
            );
            // Silent skip: a selected id with no matching config option produces
            // nothing (surfacing it is a Pass 2A/2B concern, not a render error).
            if (matched === undefined) return null;

            // Resolve the SOURCE option label for the radiogroup accessible name
            // + the sub-block header (falls back to the raw id if the source
            // option lacks a label).
            const sourceOption: OptionSetConfig["options"][number] | undefined = sourceOptions.find(
              (o) => o.id === optionId,
            );
            const optionLabel = sourceOption?.label ?? optionId;
            const optLabelId = `${widget.instanceId}-${optionId}-label`;
            const chosenRowId = chosen[optionId];

            return (
              // biome-ignore lint/a11y/useSemanticElements: section role="group" matches the Phase 6 widget-grouping convention; a native <fieldset> would inject legend styling and constrain the tile layout.
              <section
                key={optionId}
                role="group"
                aria-labelledby={optLabelId}
                className="flex flex-col gap-2"
              >
                <h4
                  id={optLabelId}
                  className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
                >
                  {optionLabel}
                </h4>
                <div
                  role="radiogroup"
                  aria-label={`${optionLabel} — choose a row`}
                  className="flex flex-col gap-2"
                >
                  {matched.rows.map((row, idx) => {
                    const isSelected = chosenRowId === row.id;
                    // Roving tabindex: the selected tile (or the first tile when
                    // none selected) is the single tab stop per radiogroup.
                    const isTabStop = isSelected || (chosenRowId === undefined && idx === 0);
                    // Positional rowColors fill ONLY when present (guarded); the
                    // static class falls back to --recipe-accent-soft otherwise.
                    const trio = config.rowColors?.[idx];
                    const tileStyle: CSSProperties | undefined =
                      isSelected && trio !== undefined
                        ? ({ "--recipe-row-bg": trio.bg, "--recipe-row-fg": trio.fg } as Record<
                            string,
                            string
                          > as CSSProperties)
                        : undefined;
                    const tileClass = isSelected
                      ? "recipe-row-accent-bg recipe-row-accent-fg rounded-md border border-[var(--recipe-accent)] p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)]"
                      : "rounded-md border border-[var(--recipe-line)] bg-[var(--recipe-card)] p-3 text-left transition-colors hover:ring-1 hover:ring-[var(--recipe-accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--recipe-accent)]";

                    return (
                      // biome-ignore lint/a11y/useSemanticElements: custom-styled buttons inside a radiogroup, not native <input type="radio"> — the tile carries a label + an inline ≤4-cell row a native radio cannot. UI-SPEC §"option-tier-matrix" + summary-checklist precedent.
                      <button
                        key={row.id}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={isTabStop ? 0 : -1}
                        onClick={() => {
                          selectRow(optionId, row.id);
                        }}
                        onKeyDown={(e) => {
                          // Standard radiogroup keyboard contract (hand-rolled,
                          // no new dep). Up/Left → previous row; Down/Right →
                          // next row; both move SELECTION (radiogroup semantics).
                          // Space/Enter select the focused tile.
                          //
                          // Focus MUST follow the roving tab stop (WAI-ARIA
                          // radiogroup pattern): the re-render that follows
                          // selectRow flips the destination tile to tabIndex=0
                          // and this tile to tabIndex=-1, so DOM focus has to
                          // move onto the destination tile or it strands on a
                          // now-untabbable node. The destination tile is the
                          // sibling at the new row index within this radiogroup
                          // (e.currentTarget.parentElement holds the tiles in
                          // rows[] order). It already exists in the DOM (only its
                          // tabIndex changes on re-render), so a synchronous
                          // .focus() is correct and React 19 preserves it across
                          // the commit.
                          const focusRowAt = (rowIdx: number): void => {
                            const sibling =
                              e.currentTarget.parentElement?.children[rowIdx];
                            if (sibling instanceof HTMLElement) sibling.focus();
                          };
                          if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                            e.preventDefault();
                            const next = matched.rows[idx + 1];
                            if (next !== undefined) {
                              selectRow(optionId, next.id);
                              focusRowAt(idx + 1);
                            }
                          } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                            e.preventDefault();
                            const prev = matched.rows[idx - 1];
                            if (prev !== undefined) {
                              selectRow(optionId, prev.id);
                              focusRowAt(idx - 1);
                            }
                          } else if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            selectRow(optionId, row.id);
                          }
                        }}
                        className={tileClass}
                        style={tileStyle}
                      >
                        <span className={`block text-sm ${isSelected ? "font-semibold" : ""}`}>
                          {row.label}
                        </span>
                        {/* row.cells[] inline ≤4-column row; cells are
                            presentational DATA (aria-hidden — the row.label is
                            the accessible name source for the radio). */}
                        <span
                          aria-hidden
                          className="mt-1 flex gap-1 text-xs text-[var(--recipe-muted)]"
                        >
                          {row.cells.map((cell, cellIdx) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: cells is a positional, never-reordered fixed tuple (schema cells.max(4)); the index IS the stable identity (no per-cell id exists). Same disposition as JSONTree.tsx.
                            <span key={`${row.id}-cell-${cellIdx}`}>{cell}</span>
                          ))}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </section>
      </CardContent>
    </Card>
  );
}
